import React, { useState, useMemo, type JSX } from "react";
import yaml from 'js-yaml';
import {
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";

// Types
interface OutlineBlock {
  id: string;
  title: string;
  goal?: string;
  context?: string;
  description?: string;
}

interface OutlineData {
  sections?: OutlineBlock[];
}

interface DraftOutlinePreviewProps {
  contentText: string;
}

interface OutlineBlockItemProps {
  block: OutlineBlock;
  isExpanded: boolean;
  onToggle: () => void;
  sectionNumber?: number; // Auto-computed number for section_* blocks
}

/**
 * DraftOutlinePreview Component
 * Renders draft_outline.yaml as hierarchical outline blocks
 * Displays block title, context, description, and goal
 */
interface ExpansionControls {
  expandedBlocks: Set<string>;
  toggleBlock: (blockId: string) => void;
  toggleAll: (expand: boolean) => void;
}

function useExpandedBlocks(sections: OutlineBlock[] | undefined): ExpansionControls {
  const [expandedBlocks, setExpandedBlocks] = useState<Set<string>>(new Set());

  const toggleBlock = (blockId: string): void => {
    const newExpanded = new Set(expandedBlocks);
    if (newExpanded.has(blockId)) {
      newExpanded.delete(blockId);
    } else {
      newExpanded.add(blockId);
    }
    setExpandedBlocks(newExpanded);
  };

  const toggleAll = (expand: boolean): void => {
    if (expand && sections) {
      setExpandedBlocks(new Set(sections.map((block) => block.id)));
    } else {
      setExpandedBlocks(new Set());
    }
  };

  return { expandedBlocks, toggleBlock, toggleAll };
}

export default function DraftOutlinePreview_Component({ contentText }: DraftOutlinePreviewProps): JSX.Element {
  // Parse YAML and handle errors
  const { parsedData, parseError } = useMemo(() => {
    try {
      const data = yaml.load(contentText) as OutlineData;
      return { parsedData: data, parseError: null };
    } catch (error) {
      return { parsedData: null, parseError: (error as Error).message };
    }
  }, [contentText]);

  // Compute section numbers for main body sections (those with section_* prefix).
  // Declared before the early returns below so the hook order stays stable across
  // renders (rules-of-hooks) — it simply yields {} while there is nothing to number.
  const sectionNumbers = useMemo(() => {
    const numbers: Record<string, number> = {};
    if (!parsedData?.sections || !Array.isArray(parsedData.sections)) {
      return numbers;
    }
    let num = 1;
    for (const block of parsedData.sections) {
      if (block.id.startsWith('section_')) {
        numbers[block.id] = num++;
      }
    }
    return numbers;
  }, [parsedData?.sections]);

  const { expandedBlocks, toggleBlock, toggleAll } = useExpandedBlocks(parsedData?.sections);

  // Error state - YAML parsing failed
  if (parseError) {
    return <ParseErrorState parseError={parseError} />;
  }

  // No parsed data or no sections
  if (!parsedData?.sections || !Array.isArray(parsedData.sections)) {
    return <NoSectionsState contentText={contentText} />;
  }

  // Render all outline sections
  return (
    <div className="h-full overflow-auto bg-cvc-canvas">
      <div className="max-w-5xl mx-auto py-6 px-4">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <OutlineToolbar onToggleAll={toggleAll} />

          <div className="divide-y divide-gray-100">
            {parsedData.sections.map((block, index) => (
              <OutlineBlockItem
                key={block.id || index}
                block={block}
                isExpanded={expandedBlocks.has(block.id)}
                onToggle={() => toggleBlock(block.id)}
                sectionNumber={sectionNumbers[block.id]}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ParseErrorState({ parseError }: { parseError: string }): JSX.Element {
  return (
    <div className="h-full overflow-auto p-6 bg-cvc-canvas">
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">Invalid YAML Format</h3>
              <p className="text-sm text-red-700 mb-3">
                The outline YAML file could not be parsed. Please check the file format.
              </p>
              <pre className="text-xs text-red-600 bg-red-100 p-3 rounded font-mono overflow-x-auto">
                {parseError}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NoSectionsState({ contentText }: { contentText: string }): JSX.Element {
  return (
    <div className="h-full overflow-auto p-6 bg-cvc-canvas">
      <div className="max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-yellow-900 mb-1">
                No Outline Sections Available
              </h4>
              <p className="text-xs text-yellow-700">
                The outline file appears to be empty or has no sections array.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <pre className="text-xs text-gray-700 font-mono leading-relaxed whitespace-pre-wrap">
            {contentText}
          </pre>
        </div>
      </div>
    </div>
  );
}

function OutlineToolbar({
  onToggleAll,
}: {
  onToggleAll: (expand: boolean) => void;
}): JSX.Element {
  return (
    <div className="p-4 border-b border-gray-100 flex items-center justify-between">
      <div className="flex items-center space-x-2">
        <ListBulletIcon className="h-5 w-5 text-purple-600" />
        <h2 className="text-base font-semibold text-gray-900">Draft Outline</h2>
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onToggleAll(true)}
          className="text-xs text-purple-600 hover:text-purple-700 font-medium"
        >
          Expand All
        </button>
        <span className="text-gray-300">|</span>
        <button
          onClick={() => onToggleAll(false)}
          className="text-xs text-purple-600 hover:text-purple-700 font-medium"
        >
          Collapse All
        </button>
      </div>
    </div>
  );
}

/**
 * Outline Block Item Component
 * Renders a single outline block with collapsible content
 */
function OutlineBlockItem({ block, isExpanded, onToggle, sectionNumber }: OutlineBlockItemProps): JSX.Element {
  // Display title with number prefix for main body sections
  const displayTitle = sectionNumber ? `${sectionNumber}. ${block.title}` : block.title;

  return (
    <div className="hover:bg-gray-50 transition-colors">
      <div
        onClick={onToggle}
        className="w-full flex items-start justify-between p-4 cursor-pointer"
      >
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline space-x-2 mb-1">
              <span className="font-medium text-gray-900 text-sm">{displayTitle}</span>
            </div>
            {!isExpanded && block.goal && (
              <div className="text-xs text-gray-600 truncate mt-1">
                {block.goal}
              </div>
            )}
          </div>
        </div>
      </div>

      {isExpanded && <OutlineBlockDetails block={block} />}
    </div>
  );
}

/** One coloured detail card inside an expanded outline block. */
function DetailCard({
  label,
  text,
  containerClass,
  labelClass,
  textClass,
}: {
  label: string;
  text: string;
  containerClass: string;
  labelClass: string;
  textClass: string;
}): JSX.Element {
  return (
    <div className={`rounded-lg p-3 border ${containerClass}`}>
      <div className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelClass}`}>
        {label}
      </div>
      <div className={`text-sm leading-relaxed ${textClass}`}>{text}</div>
    </div>
  );
}

function OutlineBlockDetails({ block }: { block: OutlineBlock }): JSX.Element {
  return (
    <div className="px-4 pb-4 pl-12">
      <div className="space-y-4">
        {block.goal && (
          <DetailCard
            label="Goal"
            text={block.goal}
            containerClass="bg-purple-50 border-purple-100"
            labelClass="text-purple-900"
            textClass="text-purple-900"
          />
        )}

        {block.context && (
          <DetailCard
            label="Context"
            text={block.context}
            containerClass="bg-blue-50 border-blue-100"
            labelClass="text-blue-900"
            textClass="text-blue-900"
          />
        )}

        {block.description && (
          <DetailCard
            label="Description"
            text={block.description}
            containerClass="bg-gray-50 border-gray-200"
            labelClass="text-gray-700"
            textClass="text-gray-900 whitespace-pre-wrap"
          />
        )}
      </div>
    </div>
  );
}
